import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  Download as DownloadIcon,
  MeetingRoom as MeetingRoomIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import Page from '../../../components/Layout/Page';
import Header from '../../../components/Layout/Header';
import EmptyTable from '../../../components/General/EmptyTable';
import CircularProgressComponent from '../../../components/Loading/CircularProgressComponent';
import { getLocations, getRoomsSchedule, getRoomsScheduleExcel } from '../../../requests/api/sections';
import handleApiResponse from '../../../utils/handleApiResponse';
import { useYear } from '../../../hooks/useYear';
import ScheduleGrid from './Components/ScheduleGrid';
import { EMPTY_SCHEDULE, LocationData, RoomsSchedule } from './types';
import EditSectionByIdModal from '../../Section/SectionListView/Modals/EditSectionByIdModal';
import { ReduxState } from '../../../types';
import { ADMINISTRATOR, SECRETARY } from '../../../utils/user_types';
import useStyles, { PRINT_PAGE_CSS } from './styles';

function RoomsScheduleView() {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { selectedYear } = useYear();
  const account = useSelector((state: ReduxState) => state.account);
  const classes = useStyles();

  const [locations, setLocations] = useState<LocationData[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const [schedule, setSchedule] = useState<RoomsSchedule>(EMPTY_SCHEDULE);
  const [loadingLocations, setLoadingLocations] = useState<boolean>(true);
  const [loadingSchedule, setLoadingSchedule] = useState<boolean>(false);
  const [sectionIdToEdit, setSectionIdToEdit] = useState<string | null>(null);
  const [updateSchedule, setUpdateSchedule] = useState<boolean>(false);
  const [downloadingExcel, setDownloadingExcel] = useState<boolean>(false);

  const canEditSections = account?.user?.userType === ADMINISTRATOR
    || account?.user?.userType === SECRETARY;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const { locations: locationsData } = await getLocations();
        setLocations(locationsData ?? []);
        if (locationsData && locationsData.length > 0) {
          setSelectedLocationId(`${locationsData[0].id}`);
        }
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  useEffect(() => {
    if (!selectedLocationId) {
      return;
    }

    const fetchSchedule = async () => {
      setLoadingSchedule(true);
      try {
        const { schedule: scheduleData } = await getRoomsSchedule({
          locationId: selectedLocationId,
          year: selectedYear,
        });
        setSchedule(scheduleData ?? EMPTY_SCHEDULE);
      } catch (err) {
        const e = err as AxiosError;
        setSchedule(EMPTY_SCHEDULE);
        handleApiResponse(enqueueSnackbar, e, false);
      } finally {
        setLoadingSchedule(false);
      }
    };

    fetchSchedule();
  }, [selectedLocationId, selectedYear, updateSchedule]);

  const handleLocationChange = (
    e: React.ChangeEvent<HTMLInputElement> | Date,
  ) => {
    setSelectedLocationId((e as React.ChangeEvent<HTMLInputElement>).target.value);
  };

  const handleSectionClick = (sectionId: number, mipreuId: number) => {
    if (canEditSections) {
      setSectionIdToEdit(`${sectionId}`);
      return;
    }

    history.push({ pathname: `/secciones/${sectionId}`, state: { mipreuId, from: '/salas' } });
  };

  const locationName = locations
    .find((location) => `${location.id}` === selectedLocationId)?.name ?? 'salas';

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const blob: Blob = await getRoomsScheduleExcel({
        locationId: selectedLocationId,
        year: selectedYear,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Salas ${locationName} ${selectedYear}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSectionUpdated = () => {
    setSectionIdToEdit(null);
    setUpdateSchedule((prevState) => !prevState);
  };

  const showLoading = loadingLocations || loadingSchedule;
  const hasData = schedule.timeBlocks.length > 0;

  return (
    <Page title="Salas">
      <Helmet>
        <style type="text/css">{PRINT_PAGE_CSS}</style>
      </Helmet>
      <Box className={classes.noPrint}>
        <Header
          title='Salas'
          icon={<MeetingRoomIcon fontSize="large" />}
          buttons={[
            {
              text: 'Imprimir',
              icon: <PrintIcon />,
              onClick: handlePrint,
              disabled: !selectedLocationId || !hasData,
            },
            {
              text: 'Descargar',
              icon: <DownloadIcon />,
              onClick: handleDownloadExcel,
              disabled: !selectedLocationId || !hasData,
              loading: downloadingExcel,
            },
          ]}
          selectors={!loadingLocations && locations.length > 0 ? [
            {
              name: 'Sede',
              keyField: 'Sede',
              value: selectedLocationId,
              options: locations.map((location) => ({
                id: `${location.id}`,
                name: location.name,
              })),
              onChange: handleLocationChange,
            },
          ] : undefined}
        />
      </Box>
      {showLoading ? <CircularProgressComponent /> : <Box mt={3} className={classes.printArea}>
        {!loadingLocations && locations.length === 0 && <EmptyTable />}
        {locations.length > 0 && hasData && <ScheduleGrid
          schedule={schedule}
          onSectionClick={handleSectionClick}
          printTitle={`${locationName.toUpperCase()} ${selectedYear}`}
        />}
        {locations.length > 0 && !hasData && <Box>
          <EmptyTable />
          <Typography variant='body2' mt={2} textAlign='center'>
            Esta sede no tiene secciones con horario asignado para el año {selectedYear}.
          </Typography>
        </Box>}
      </Box>}
      <EditSectionByIdModal
        sectionId={sectionIdToEdit}
        onClose={() => setSectionIdToEdit(null)}
        onUpdated={handleSectionUpdated}
      />
    </Page>
  );
}

export default RoomsScheduleView;
